"use strict";
/**
 * Deterministic input validator. Returns issues as a flat array so
 * the UI can render them inline per-field.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.highReturnAssumptionWarning = exports.RETURN_INPUT_WARNING_ABOVE_PCT = exports.RETURN_INPUT_MAX_PCT = exports.validateInputs = void 0;
const MAX_MONEY = 1e11; // ₹10,000 crore hard cap
const MAX_AGE = 100;
const MIN_AGE = 18;
function bounded(v, min, max) {
    return Number.isFinite(v) && v >= min && v <= max;
}
function validateInputs(i) {
    const issues = [];
    const push = (field, message) => issues.push({ field, message });
    if (!bounded(i.currentAge, MIN_AGE, MAX_AGE)) {
        push("currentAge", `Current age must be between ${MIN_AGE} and ${MAX_AGE}.`);
    }
    if (!bounded(i.retirementAge, MIN_AGE, MAX_AGE)) {
        push("retirementAge", `Retirement age must be between ${MIN_AGE} and ${MAX_AGE}.`);
    }
    if (!bounded(i.planIncomeUntilAge, MIN_AGE, MAX_AGE + 20)) {
        push("planIncomeUntilAge", `Planning horizon must be between ${MIN_AGE} and ${MAX_AGE + 20}.`);
    }
    if (i.retirementAge < i.currentAge) {
        push("cross:retirementAge", "Retirement age cannot be less than current age.");
    }
    if (i.planIncomeUntilAge <= i.retirementAge) {
        push("cross:planIncomeUntilAge", "Plan income until age must be greater than retirement age.");
    }
    if (i.incomeBeginsAtAge < i.retirementAge) {
        push("cross:incomeBeginsAtAge", "Retirement income cannot begin before retirement.");
    }
    if (i.incomeEndsAtAge < i.incomeBeginsAtAge) {
        push("cross:incomeEndsAtAge", "Retirement income cannot end before it begins.");
    }
    if (i.incomeEndsAtAge > i.planIncomeUntilAge) {
        push("cross:incomeEndsAtAge", "Retirement income cannot continue past the planning horizon.");
    }
    const moneyFields = [
        "currentMonthlyExpenses",
        "desiredRetirementLifestyleToday",
        "existingRetirementInvestments",
        "currentMonthlySip",
        "lumpSumAvailableNow",
        "expectedLumpSumAtRetirement",
        "monthlyDependableIncomeAtStart",
        "healthcareEmergencyReserveAtRetirement",
        "oneTimeExpenseAtRetirement",
        "desiredCorpusRemainingAtPlanEnd",
    ];
    for (const f of moneyFields) {
        const v = i[f];
        if (!Number.isFinite(v) || v < 0 || v > MAX_MONEY) {
            push(String(f), "Enter a valid amount between 0 and ₹10,000 crore.");
        }
    }
    // Return-input boundaries — user-input safety ceiling of 18% per
    // 2026-07-27 leadership decision (see
    // .lovable/blog-improvement/retirement-cluster/calculator-return-input-governance.md).
    // FinEdge-controlled defaults/samples remain ≤ 13%; the calculator
    // accepts user-entered assumptions up to 18% inclusive and rejects
    // anything above 18% without silent clamping.
    const rateFields = [
        ["preRetirementInflation", 0, 0.25],
        ["postRetirementInflation", 0, 0.25],
        ["accumulationReturn", -0.05, 0.18, "Enter a return assumption of 18% or lower."],
        ["withdrawalReturn", -0.05, 0.18, "Enter a return assumption of 18% or lower."],
        ["annualSipStepUp", 0, 0.50],
        ["annualIncomeEscalation", 0, 0.30],
    ];
    for (const [f, min, max, customMsg] of rateFields) {
        const v = i[f];
        if (!bounded(v, min, max)) {
            push(String(f), customMsg ??
                `Enter a rate between ${(min * 100).toFixed(0)}% and ${(max * 100).toFixed(0)}%.`);
        }
    }
    return issues;
}
exports.validateInputs = validateInputs;
/**
 * Non-blocking "high assumption" thresholds for the two return-input
 * fields. Values above 13% and up to 18% are valid (the engine will
 * calculate) but the UI must surface a warning per governance record.
 */
exports.RETURN_INPUT_MAX_PCT = 18;
exports.RETURN_INPUT_WARNING_ABOVE_PCT = 13;
function highReturnAssumptionWarning(field, valueDecimal) {
    if (!Number.isFinite(valueDecimal))
        return undefined;
    const pct = valueDecimal * 100;
    if (pct <= exports.RETURN_INPUT_WARNING_ABOVE_PCT)
        return undefined;
    if (pct > exports.RETURN_INPUT_MAX_PCT)
        return undefined; // hard error handled by validateInputs
    if (field === "accumulationReturn") {
        return "This is a high return assumption. Actual market-linked returns may be lower or higher. Test your plan at a lower return as well.";
    }
    return "This is a high post-retirement return assumption. It may materially overstate how long the corpus could last. Test a lower-return scenario as well.";
}
exports.highReturnAssumptionWarning = highReturnAssumptionWarning;
